// src/components/Layout/Header.tsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../store/store";
import { logout } from "../../store/slices/authSlice";
import globalStyle from "../../styles/globalStyle";

import logo from "../../assets/logo.png";
import {
  UserIcon,
  UsersIcon,
  ArrowRightOnRectangleIcon,
  ArrowLeftOnRectangleIcon,
  ArrowRightIcon,
  GlobeAltIcon,
  CheckIcon,
  XMarkIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import Button from "../UI/Button";

const LANGUAGES = [
  // European
  { code: "en", name: "English", flag: "🇺🇸" },
  { code: "fr", name: "Français", flag: "🇫🇷" },
  { code: "de", name: "Deutsch", flag: "🇩🇪" },
  { code: "es", name: "Español", flag: "🇪🇸" },
  { code: "it", name: "Italiano", flag: "🇮🇹" },
  { code: "pt", name: "Português", flag: "🇵🇹" },
  { code: "ru", name: "Русский", flag: "🇷🇺" },
  { code: "nl", name: "Nederlands", flag: "🇳🇱" },
  { code: "pl", name: "Polski", flag: "🇵🇱" },
  { code: "uk", name: "Українська", flag: "🇺🇦" },
  { code: "tr", name: "Türkçe", flag: "🇹🇷" },
  { code: "ro", name: "Română", flag: "🇷🇴" },
  { code: "sv", name: "Svenska", flag: "🇸🇪" },
  { code: "el", name: "Ελληνικά", flag: "🇬🇷" },
  { code: "ka", name: "ქართული", flag: "🇬🇪" },
  // Middle East & Africa
  { code: "ar", name: "العربية", flag: "🇸🇦" },
  { code: "fa", name: "فارسی", flag: "🇮🇷" },
  { code: "he", name: "עברית", flag: "🇮🇱" },
  { code: "sw", name: "Kiswahili", flag: "🇰🇪" },
  // South Asia
  { code: "hi", name: "हिन्दी", flag: "🇮🇳" },
  { code: "bn", name: "বাংলা", flag: "🇧🇩" },
  { code: "ur", name: "اردو", flag: "🇵🇰" },
  // East Asia
  { code: "zh-CN", name: "中文 (简体)", flag: "🇨🇳" },
  { code: "zh-TW", name: "中文 (繁體)", flag: "🇹🇼" },
  { code: "ja", name: "日本語", flag: "🇯🇵" },
  { code: "ko", name: "한국어", flag: "🇰🇷" },
  // Southeast Asia
  { code: "vi", name: "Tiếng Việt", flag: "🇻🇳" },
  { code: "th", name: "ภาษาไทย", flag: "🇹🇭" },
  { code: "tl", name: "Filipino", flag: "🇵🇭" },
  { code: "id", name: "Bahasa Indonesia", flag: "🇮🇩" },
  { code: "ms", name: "Bahasa Melayu", flag: "🇲🇾" },
  { code: "my", name: "မြန်မာဘာသာ", flag: "🇲🇲" },
  { code: "km", name: "ភាសាខ្មែរ", flag: "🇰🇭" },
  { code: "lo", name: "ພາສາລາວ", flag: "🇱🇦" },
  // Americas
  { code: "pt-BR", name: "Português (BR)", flag: "🇧🇷" },
];

// ─── Translation engine ───────────────────────────────────────────────────────

const originalTexts = new Map<Text, string>();
let currentLang = "en";

export function getCurrentLang(): string {
  return currentLang;
}
let isTranslating = false;

let mutationDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let domObserver: MutationObserver | null = null;

export async function translateText(
  text: string,
  targetLang: string,
): Promise<string> {
  if (!text.trim()) return text;
  try {
    const url =
      `https://translate.googleapis.com/translate_a/single` +
      `?client=gtx&sl=en&tl=${targetLang}&dt=t&q=${encodeURIComponent(text)}`;
    const res = await fetch(url);
    if (!res.ok) return text;
    const data = await res.json();
    const translated =
      (data?.[0] as any[])?.map((chunk: any) => chunk?.[0] ?? "").join("") ??
      "";
    return translated || text;
  } catch {
    return text;
  }
}

function getTextNodes(root: Node): Text[] {
  const nodes: Text[] = [];
  const skipTags = new Set([
    "SCRIPT",
    "STYLE",
    "NOSCRIPT",
    "IFRAME",
    "INPUT",
    "TEXTAREA",
  ]);
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement;
      if (!parent) return NodeFilter.FILTER_REJECT;
      if (skipTags.has(parent.tagName)) return NodeFilter.FILTER_REJECT;
      if (!node.textContent?.trim()) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let node: Node | null;
  while ((node = walker.nextNode())) nodes.push(node as Text);
  return nodes;
}

async function applyTranslation(
  langCode: string,
  onProgress?: (pct: number) => void,
) {
  if (isTranslating) return;
  isTranslating = true;

  const root = document.getElementById("root");
  if (!root) {
    isTranslating = false;
    return;
  }

  originalTexts.forEach((original, node) => {
    if (node.parentNode) node.textContent = original;
  });

  if (langCode === "en") {
    currentLang = "en";
    isTranslating = false;
    onProgress?.(100);
    return;
  }

  const nodes = getTextNodes(root);
  nodes.forEach((node) => {
    if (!originalTexts.has(node))
      originalTexts.set(node, node.textContent || "");
  });

  const BATCH = 10;
  for (let i = 0; i < nodes.length; i += BATCH) {
    const batch = nodes.slice(i, i + BATCH);
    await Promise.all(
      batch.map(async (node) => {
        const english = originalTexts.get(node) || node.textContent || "";
        const translated = await translateText(english, langCode);
        if (node.parentNode) node.textContent = translated;
      }),
    );
    onProgress?.(Math.round(((i + BATCH) / nodes.length) * 100));
  }

  currentLang = langCode;
  isTranslating = false;
  onProgress?.(100);
}

async function translateNewNodes(addedNodes: NodeList, langCode: string) {
  if (langCode === "en" || isTranslating) return;

  const skipTags = new Set([
    "SCRIPT",
    "STYLE",
    "NOSCRIPT",
    "IFRAME",
    "INPUT",
    "TEXTAREA",
  ]);
  const newTextNodes: Text[] = [];

  addedNodes.forEach((added) => {
    const walker = document.createTreeWalker(added, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        const parent = node.parentElement;
        if (!parent) return NodeFilter.FILTER_REJECT;
        if (skipTags.has(parent.tagName)) return NodeFilter.FILTER_REJECT;
        if (!node.textContent?.trim()) return NodeFilter.FILTER_REJECT;
        if (originalTexts.has(node as Text)) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      },
    });
    let node: Node | null;
    while ((node = walker.nextNode())) newTextNodes.push(node as Text);
  });

  if (newTextNodes.length === 0) return;

  newTextNodes.forEach((node) => {
    if (!originalTexts.has(node))
      originalTexts.set(node, node.textContent || "");
  });

  const BATCH = 10;
  for (let i = 0; i < newTextNodes.length; i += BATCH) {
    const batch = newTextNodes.slice(i, i + BATCH);
    await Promise.all(
      batch.map(async (node) => {
        const english = originalTexts.get(node) || node.textContent || "";
        const translated = await translateText(english, langCode);
        if (node.parentNode) node.textContent = translated;
      }),
    );
  }
}

function startDomObserver() {
  if (domObserver) return;

  domObserver = new MutationObserver((mutations) => {
    if (currentLang === "en") return;

    const allAdded: Node[] = [];
    mutations.forEach((m) => {
      m.addedNodes.forEach((n) => {
        if (n.nodeType === Node.ELEMENT_NODE) allAdded.push(n);
      });
    });

    if (allAdded.length === 0) return;

    if (mutationDebounceTimer) clearTimeout(mutationDebounceTimer);
    mutationDebounceTimer = setTimeout(() => {
      const nodeList = {
        length: allAdded.length,
        forEach: (cb: (n: Node) => void) => allAdded.forEach(cb),
      } as unknown as NodeList;
      translateNewNodes(nodeList, currentLang);
    }, 150);
  });

  const root = document.getElementById("root");
  if (root) domObserver.observe(root, { childList: true, subtree: true });
}

function stopDomObserver() {
  if (domObserver) {
    domObserver.disconnect();
    domObserver = null;
  }
}

// ─── Google Translate Component ───────────────────────────────────────────────

const GoogleTranslate: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeLang, setActiveLang] = useState("en");
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const translatorRef = useRef<HTMLDivElement>(null);

  const filteredLanguages = LANGUAGES.filter(
    (lang) =>
      lang.name.toLowerCase().includes(search.toLowerCase()) ||
      lang.code.toLowerCase().includes(search.toLowerCase()),
  );

  useEffect(() => {
    startDomObserver();
    const saved = localStorage.getItem("regalflux_language") || "en";
    setActiveLang(saved);
    if (saved !== "en") {
      setBusy(true);
      applyTranslation(saved, setProgress).finally(() => setBusy(false));
    }
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        translatorRef.current &&
        !translatorRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const changeLanguage = async (langCode: string) => {
    if (langCode === activeLang || busy) {
      setIsOpen(false);
      setSearch("");
      return;
    }
    setIsOpen(false);
    setSearch("");
    setBusy(true);
    setProgress(0);
    setActiveLang(langCode);
    localStorage.setItem("regalflux_language", langCode);
    await applyTranslation(langCode, setProgress);
    window.dispatchEvent(new Event("regalflux_lang_change"));
    setBusy(false);
  };

  const resetToEnglish = async () => {
    if (busy) return;
    setIsOpen(false);
    setSearch("");
    setBusy(true);
    setActiveLang("en");
    localStorage.setItem("regalflux_language", "en");
    await applyTranslation("en", setProgress);
    window.dispatchEvent(new Event("regalflux_lang_change"));
    setBusy(false);
  };

  const activeLanguage = LANGUAGES.find((l) => l.code === activeLang);

  return (
    <div ref={translatorRef} className="relative">
      {busy && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            height: 3,
            zIndex: 99999,
            background: "linear-gradient(90deg,#f97316,#fb923c)",
            width: `${progress}%`,
            transition: "width 0.3s ease",
          }}
        />
      )}

      {isOpen && (
        <div
          className={`absolute ${globalStyle.card} animate-slide-in-left w-72 md:w-80 right-0 top-12 md:top-14 mt-2 z-50`}
        >
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 gradient-primary rounded-lg flex items-center justify-center">
                  <GlobeAltIcon className="w-5 h-5 text-black" />
                </div>
                <h3 className={globalStyle.h6}>Language</h3>
              </div>
              <button
                onClick={() => {
                  setIsOpen(false);
                  setSearch("");
                }}
                className="p-1 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            <input
              type="text"
              placeholder="Search language..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full mb-3 px-3 py-2 text-sm bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
            />

            <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
              {filteredLanguages.length === 0 ? (
                <p className="text-xs text-gray-500 text-center py-4">
                  No languages found
                </p>
              ) : (
                filteredLanguages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    disabled={busy}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all ${globalStyle.transition} ${
                      activeLang === lang.code
                        ? "bg-gradient-to-r from-orange-500/10 to-orange-600/10 border border-orange-500/30"
                        : "hover:bg-gray-800 border border-transparent hover:border-gray-700"
                    } ${busy ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <div className="flex items-center space-x-2">
                      <span className="text-xl">{lang.flag}</span>
                      <span className="text-sm font-medium text-white">
                        {lang.name}
                      </span>
                    </div>
                    {activeLang === lang.code ? (
                      <CheckIcon className="w-4 h-4 text-orange-500 flex-shrink-0" />
                    ) : (
                      <div className="w-1.5 h-1.5 rounded-full bg-gray-600 flex-shrink-0" />
                    )}
                  </button>
                ))
              )}
            </div>

            {activeLang !== "en" && (
              <button
                onClick={resetToEnglish}
                disabled={busy}
                className={`w-full mt-3 flex items-center justify-center space-x-2 px-3 py-2 text-xs font-medium text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg ${globalStyle.transition} border border-gray-700`}
              >
                <ArrowPathIcon className="w-3.5 h-3.5" />
                <span>Reset to English</span>
              </button>
            )}

            <div className="mt-3 pt-3 border-t border-gray-800">
              <p className="text-xs text-gray-500 text-center">
                {busy
                  ? `Translating… ${progress}%`
                  : `${LANGUAGES.length} languages • Powered by Google Translate`}
              </p>
            </div>
          </div>
        </div>
      )}

      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={busy}
        className={`p-1.5 md:p-2 rounded-lg hover:bg-gray-800 transition-colors relative group ${busy ? "animate-pulse" : ""}`}
        title="Translate"
      >
        {busy ? (
          <svg
            className="w-5 h-5 md:w-6 md:h-6 text-orange-500 animate-spin"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : (
          <>
            <GlobeAltIcon className="w-5 h-5 md:w-6 md:h-6 text-white" />
            {activeLang !== "en" && activeLanguage && (
              <span className="absolute -top-1 -right-1 text-xs leading-none">
                {activeLanguage.flag}
              </span>
            )}
            <div className="hidden md:block absolute -bottom-8 left-1/2 -translate-x-1/2 bg-gray-900 text-primary text-xs px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity border border-gray-700">
              Translate
            </div>
          </>
        )}
      </button>
    </div>
  );
};

// ─── Main Header ──────────────────────────────────────────────────────────────

const Header: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);

  const siteName = process.env.REACT_APP_SITE_NAME || "XRP Trading Platform";
  const isAdmin = user?.role === "admin";
  const isOnAdminDashboard = window.location.pathname.startsWith("/admin/");
  const location = window.location.pathname;

  const hideTranslator = [
    "/login",
    "/register",
    "/forgot-password",
    "/verify-email",
    "/reset-password",
  ].includes(location);

  const handleLogout = async () => {
    await dispatch(logout());
    navigate("/login");
  };

  return (
    <header
      className={`sticky top-0 z-40 bg-black border-b ${globalStyle.border} ${globalStyle.shadowMd}`}
    >
      <div className="px-4 md:px-6 lg:px-8 py-3 md:py-4">
        <div className="flex items-center justify-between">
          {/* Left */}
          <div className="flex items-center gap-5">
            <img
              src={logo}
              alt={siteName}
              className="w-12 h-12 md:w-14 md:h-14"
            />
            <div>
              <h1 className={globalStyle.h5}>{siteName}</h1>
              {user && (
                <p className={globalStyle.caption}>
                  {user.username} • {isAdmin ? "Admin" : "User"}
                </p>
              )}
            </div>
          </div>

          {/* Right */}
          {user ? (
            <div className="flex items-center space-x-1 md:space-x-3">
              {!hideTranslator && <GoogleTranslate />}

              {user.xrp_balance !== undefined && (
                <div className="hidden md:flex items-center space-x-2 px-3 py-1.5 bg-primary-900 rounded-lg">
                  <span className="text-sm font-medium text-primary">
                    {(typeof user.xrp_balance === "number"
                      ? user.xrp_balance
                      : parseFloat(user.xrp_balance || "0")
                    ).toFixed(2)}{" "}
                    XRP
                  </span>
                </div>
              )}

              {isAdmin && (
                <button
                  onClick={() =>
                    navigate(
                      isOnAdminDashboard ? "/dashboard" : "/admin/dashboard",
                    )
                  }
                  className="p-2 rounded-lg hover:bg-gray-800 transition-colors relative group"
                  title={
                    isOnAdminDashboard
                      ? "Switch to User Dashboard"
                      : "Switch to Admin Dashboard"
                  }
                >
                  {isOnAdminDashboard ? (
                    <UserIcon className="w-5 h-5 md:w-6 md:h-6 text-white" />
                  ) : (
                    <UsersIcon className="w-5 h-5 md:w-6 md:h-6 text-white" />
                  )}
                  <div className="hidden md:block absolute -bottom-8 left-1/2 -translate-x-1/2 bg-gray-900 text-primary text-xs px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity border border-gray-700">
                    {isOnAdminDashboard
                      ? "Switch to User Dashboard"
                      : "Switch to Admin Dashboard"}
                  </div>
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-black rounded-full text-xs flex items-center justify-center md:hidden font-bold">
                    {isOnAdminDashboard ? "U" : "A"}
                  </div>
                </button>
              )}

              <div className="hidden md:flex items-center space-x-2">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className={globalStyle.body2}>Online</span>
              </div>

              <button
                onClick={handleLogout}
                className="p-2 rounded-lg hover:bg-gray-800 transition-colors relative group"
                title="Logout"
              >
                <ArrowRightOnRectangleIcon className="w-5 h-5 md:w-6 md:h-6 text-white hidden md:block" />
                <div className="hidden md:block absolute -bottom-8 left-1/2 -translate-x-1/2 bg-gray-900 text-primary text-xs px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity border border-gray-700">
                  Logout
                </div>
                <ArrowLeftOnRectangleIcon className="w-5 h-5 text-white md:hidden" />
              </button>
            </div>
          ) : (
            <div className="flex items-center space-x-3">
              {location === "/" && !hideTranslator && <GoogleTranslate />}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate("/login")}
                className="hidden md:inline-flex"
              >
                Login
              </Button>
              <Button
                size="sm"
                onClick={() => navigate("/register")}
                className="hidden md:inline-flex"
                rightIcon={<ArrowRightIcon className="w-4 h-4 ml-1" />}
              >
                Register
              </Button>
              <button
                onClick={() => navigate("/login")}
                className="md:hidden p-2 rounded-lg hover:bg-gray-800 transition-colors"
                title="Login"
              >
                <ArrowRightOnRectangleIcon className="w-5 h-5 text-white" />
              </button>
            </div>
          )}
        </div>

        {user && user.xrp_balance !== undefined && (
          <div className="mt-2 md:hidden flex items-center justify-between px-2">
            <span className="text-sm font-medium text-primary">
              Balance:{" "}
              {(typeof user.xrp_balance === "number"
                ? user.xrp_balance
                : parseFloat(user.xrp_balance || "0")
              ).toFixed(2)}{" "}
              XRP
            </span>
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-xs text-gray-400">Online</span>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
